import { makeStyles } from "@material-ui/core";
import ModalWrapper from "@View/components/ModalWrapper";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ModalTittle from "@View/components/ModalTittle";
import { downloadFinDataTemplateFileFunc, downloadFinDataV1TemplateFileFunc } from "@Utils/downloadLinks";
import NButton from "@View/components/ui/NButton";
import FileUploader from "@View/components/ui/FileUploader";
import CommonError from "@View/pages/AdminPO/customer/customerDetails/bankStatement/CommonError";
import UploadResultsView from "@View/pages/AdminPO/customer/customerDetails/bankStatement/UploadResultsView";
import RequestsTable from "@View/pages/AdminPO/customer/customerDetails/RequestsTable";
import { formatDate, formatDateTime } from "@Localization/utils";
import { useButtonStyles } from "@View/pages/AdminPO/customer/customerDetails/bankStatement/styles";
import RequestSearcher from "@View/components/ui/RequestSearcher";
import CommonProgressLoader from "@View/components/ui/CommonProgressLoader";
import { Checkbox } from "@Components/form/control/Checkbox";
import { useAlert } from "@View/components/ui/alert/alert-context";
import { useWebSocketDocumentUploader } from "@Utils/websocketDocumentUploader";

const acceptedFormats = ["xlsx", "xml", "txt"];
const maxFileSize = 50;

const useStyles = makeStyles((_theme) => ({
  cover: {
    color: "#54606F",
    fontSize: "13px",
    marginTop: "15px",
    "& > div + div": {
      marginTop: "10px",
    },
  },
  requests: {
    maxHeight: "300px",
    overflow: "auto",
    boxSizing: "border-box",
  },
  divider: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    fontWeight: 600,
    marginBottom: "25px",
  },
  goToAll: {
    cursor: "pointer",
    color: "#6FA4F2",
    fontWeight: 600,
  },
  selectedItem: {
    border: "1px solid #A1ADBA",
    borderRadius: "10px",
    padding: "10px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
  },
}));

const BankStatementsModal = ({ open, handleClose, client }) => {
  const userId = client.id;

  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState(1);
  const [search, setSearch] = React.useState("");
  const [notLoadAddition, setNotLoadAddition] = React.useState(false);
  const [savedError, setSavedError] = React.useState(null); // Сохраняем ошибку для показа после разрыва соединения
  const [savedResults, setSavedResults] = React.useState(null); // Сохраняем результаты для показа после разрыва соединения

  const classes = useStyles();
  const btnClasses = useButtonStyles();

  const [isError, setIsError] = React.useState(false);

  const [selectedItems, setSelectedItems] = useState([]);

  const selectedItemsData = useMemo(() => {
    if (!selectedItems.length) return null;
    return selectedItems.map((id) =>
      client.finDataRequests.find((i) => i.id === id)
    );
  }, [selectedItems, client]);

  const tableItems = useMemo(() => {
    if (search.length < 2) return client.finDataRequests;

    return client.finDataRequests.filter((i) =>
      i.customerName.toLowerCase().includes(search.toLowerCase())
    );
  }, [client, search]);

  const { showAlert } = useAlert();

  const {
    isConnecting,
    isConnected,
    progress,
    status,
    error: wsError,
    success: wsSuccess,
    results,
    connect,
    uploadFiles,
    processFiles,
    disconnect,
    reset
  } = useWebSocketDocumentUploader();

  // Сохраняем ошибку и результаты перед разрывом WebSocket-соединения,
  // чтобы они оставались на экране после закрытия сокета.
  useEffect(() => {
    if (wsError && !savedError) {
      setSavedError(wsError);
    }
  }, [wsError, savedError]);

  useEffect(() => {
    if (results && results.details && !savedResults) {
      setSavedResults(results);
    }
  }, [results, savedResults]);

  // Итоговые данные для блока результатов.
  const resultData = savedResults || results;
  const errorMessage = wsError || savedError;
  const hasResults = !!resultData?.details;

  // Используем WebSocket для отправки файлов
  const submitForm = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      showAlert({
        title: t("snackbar.error"),
        text: "Не выбраны файлы для загрузки",
        level: "error",
      });
      return;
    }

    try {
      setIsLoading(true);

      // Подключаемся к WebSocket
      await connect({
        clientId: client._leadId ? undefined : userId,
        leadId: client._leadId,
        requestIds: selectedItems,
        requireInnInOSV: notLoadAddition,
      });

      // Загружаем файлы (обработка начнется автоматически после подтверждения)
      await uploadFiles(selectedFiles);

    } catch (error) {
      console.error("Ошибка WebSocket загрузки:", error);
      showAlert({
        title: t("snackbar.error"),
        text: error.message || "Ошибка при загрузке файлов",
        level: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Обработчик закрытия модалки
  const handleModalClose = useCallback(() => {
    reset(); // Полностью сбрасываем состояние WebSocket
    setIsLoading(false); // Сбрасываем состояние загрузки
    setSavedError(null); // Сбрасываем сохраненную ошибку
    setSavedResults(null); // Сбрасываем сохраненные результаты
    handleClose();
  }, [reset, handleClose]);

  function goToFirstStep() {
    setCurrentStep(1);
    setSelectedItems([]);
  }
  function goToSecondStep() {
    setCurrentStep(2);
  }

  // Сброс состояния WebSocket при открытии модалки
  useEffect(() => {
    if (open) {
      reset(); // Полностью сбрасываем состояние WebSocket
      setIsLoading(false); // Сбрасываем состояние загрузки
      setSelectedFiles([]); // Сбрасываем выбранные файлы
      setCurrentStep(1); // Возвращаемся к первому шагу
      setSelectedItems([]); // Сбрасываем выбранные элементы
      setSearch(""); // Сбрасываем поиск
      setNotLoadAddition(false); // Сбрасываем чекбокс
      setIsError(false); // Сбрасываем ошибки
      setSavedError(null); // Сбрасываем сохраненную ошибку
      setSavedResults(null); // Сбрасываем сохраненные результаты
    }
  }, [open, reset]);

  // Cleanup при размонтировании компонента
  useEffect(() => {
    return () => {
      reset(); // Полностью сбрасываем состояние при размонтировании
    };
  }, [reset]);

  return (
    <ModalWrapper open={open} handleClose={handleModalClose} maxWidth={"720px"}>
      <ModalTittle>finData1C.title</ModalTittle>
      {isError ?
        (
          <CommonError closeError={() => setIsError(false)} />
        ) : (isLoading || isConnecting || isConnected || wsSuccess || errorMessage || hasResults) ? (
          <>
            {/* Лоадер с прогрессом — пока нет ни результатов, ни ошибки */}
            {!hasResults && !errorMessage && (
              <CommonProgressLoader progress={progress} status={status} />
            )}

            {/* Отображение результатов */}
            {(hasResults || errorMessage) && (
              <UploadResultsView
                resultData={resultData}
                errorMessage={errorMessage}
                onClose={handleModalClose}
              />
            )}
          </>
        ) : (
          <div className={classes.cover}>
            {currentStep === 1 ? (
              <>
                <RequestSearcher
                  value={search}
                  updateValue={setSearch}
                  selectAction={goToSecondStep}
                />
                <div>{t("bankStatement.currentRequests")}</div>
                <div className={classes.requests}>
                  <RequestsTable
                    selectedItems={selectedItems}
                    changeSelection={setSelectedItems}
                    items={tableItems}
                  />
                </div>
                <div style={{ marginTop: "35px" }}>
                  <button
                    className={btnClasses.addBtn}
                    disabled={!selectedItems.length}
                    onClick={() => goToSecondStep()}
                  >
                    {t("bankStatement.add")}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={classes.divider}>
                  <div style={{ color: "#A1ADBA" }}>
                    {selectedItems.length ? null : t("bankStatement.newRequest")}
                  </div>
                  <div>
                    <span
                      className={classes.goToAll}
                      onClick={() => goToFirstStep()}
                    >
                      {t("bankStatement.goToAll")}
                    </span>
                  </div>
                </div>
                {selectedItems.length ? (
                  <div style={{ marginBottom: "25px" }}>
                    {selectedItemsData.map((itemData) => (
                      <div className={classes.selectedItem}>
                        <div>
                          <div
                            style={{
                              color: "#1A1E24",
                              fontWeight: 600,
                              paddingBottom: "5px",
                            }}
                          >
                            {itemData.customerName}
                          </div>
                          <div style={{ color: "#54606F" }}>
                            {formatDate(itemData.startDate)} -{" "}
                            {formatDate(itemData.endDate)}
                          </div>
                        </div>
                        <div style={{ color: "#A1ADBA" }}>
                          {t("bankStatement.updated")}{" "}
                          {formatDateTime(itemData.updatedAt)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div>{t("finData1C.text_1")}</div>
                <div>
                  <ol>
                    <li>
                      {t("finData1C.list.item_1")}{" "}
                      <a
                        href="#"
                        style={{ width: "100%" }}
                        onClick={() => downloadFinDataV1TemplateFileFunc(client.externalId)}
                      >
                        {t("buttons.fileExample")}
                      </a>

                    </li>
                    <li>{t("finData1C.list.item_2")}</li>
                    <li>{t("finData1C.list.item_3")}</li>
                  </ol>
                </div>
                <Checkbox
                  value={notLoadAddition}
                  onChange={(v) => setNotLoadAddition(v)}
                  label={t("finData1C.not_load")}
                  plain
                />
                <FileUploader
                  acceptedFormats={acceptedFormats}
                  maxFileSize={maxFileSize}
                  multiple
                  onChange={(file) => setSelectedFiles(file)}
                />
                <div style={{ marginTop: "15px", marginBottom: "-10px" }}>
                  <NButton
                    disabled={!selectedFiles.length || isConnecting || isConnected || wsSuccess}
                    styles={{ maxWidth: "270px", margin: "0 auto" }}
                    onClick={() => submitForm()}
                  >
                    {isConnecting ? "Подключение..." :
                      isConnected ? "Обработка..." :
                        wsSuccess ? "Завершено" :
                          t("attach.upload")}
                  </NButton>
                </div>
              </>
            )}
          </div>
        )}
    </ModalWrapper>
  );
};

export default BankStatementsModal;
